import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import GoBack from "../components/GoBack";
import {
  PaymentMethodRow,
  AddCardModal,
  AddBankModal,
  AuthorizeMandateModal,
} from "../components/payment";
import { usePaymentMethods } from "../hooks/usePaymentMethods";

const PaymentMethods: React.FC = () => {
  const { t } = useTranslation();
  const { user, refreshUser } = useAuth();
  const {
    methods,
    loading,
    error,
    successMessage,
    busyId,
    refresh,
    setDefault,
    remove,
    addMandate,
    flash,
  } = usePaymentMethods();

  const [showAddCard, setShowAddCard] = useState(false);
  const [showAddBank, setShowAddBank] = useState(false);
  // The bank id awaiting mandate authorization (disclosure modal open).
  const [mandateTargetId, setMandateTargetId] = useState<number | null>(null);

  // Whether an unauthorized bank account exists. Checks the API flag as well
  // as the loaded methods so the banner shows immediately, before the list
  // finishes loading.
  //
  // Advisory only — nothing here blocks navigation. The app-wide redirect
  // this page used to enforce was removed; see ProtectedRoute.
  const showMandateWarning =
    !!user?.hasBankWithoutMandate || methods.some((m) => m.requiresMandate);

  return (
    <div className="mt-6">
      <GoBack to="/dashboard" />

      {showMandateWarning && (
        <div className="mb-6 rounded-lg border border-warning bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <strong className="block mb-1">
            {t(
              "paymentMethods.mandateRequiredTitle",
              "Action required: authorize your bank account",
            )}
          </strong>
          {t(
            "paymentMethods.mandateRequiredBody",
            "One of your bank accounts needs ACH authorization. Authorize it below, or add a new bank account and remove the old one, so future payments aren't interrupted.",
          )}
        </div>
      )}
      <div className="flex justify-between items-center mb-6">
        <h2>{t("paymentMethods.title")}</h2>
        <div className="flex gap-2">
          <button
            className="rounded-lg border border-primary px-4 py-2 text-sm font-medium text-primary transition-colors hover:bg-primary/5 cursor-pointer"
            onClick={() => setShowAddBank(true)}
          >
            {t("paymentMethods.addBank")}
          </button>
          <button
            className="rounded-lg bg-primary px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary-light cursor-pointer"
            onClick={() => setShowAddCard(true)}
          >
            {t("paymentMethods.addCard")}
          </button>
        </div>
      </div>

      {successMessage && (
        <div className="mb-4 rounded-lg bg-green-50 px-4 py-3 text-sm text-success">
          {successMessage}
        </div>
      )}
      {error && (
        <div className="mb-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-danger">
          {error}
        </div>
      )}

      <div className="rounded-xl bg-white shadow-sm">
        <div className="p-5">
          {loading ? (
            <p className="text-sm text-muted">{t("paymentMethods.loading")}</p>
          ) : methods.length === 0 ? (
            <div className="text-center py-10">
              <div className="mb-4 text-[3rem]">💳</div>
              <h5 className="text-muted">{t("paymentMethods.noMethods")}</h5>
              <p className="text-muted mb-6">
                {t("paymentMethods.noMethodsDescription")}
              </p>
              <div className="flex justify-center gap-2">
                <button
                  className="rounded-lg border border-primary px-4 py-2 text-sm font-medium text-primary transition-colors hover:bg-primary/5 cursor-pointer"
                  onClick={() => setShowAddBank(true)}
                >
                  {t("paymentMethods.addBank")}
                </button>
                <button
                  className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-light cursor-pointer"
                  onClick={() => setShowAddCard(true)}
                >
                  {t("paymentMethods.addCard")}
                </button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {methods.map((m) => (
                <PaymentMethodRow
                  key={m.id}
                  method={m}
                  onSetDefault={setDefault}
                  onRemove={remove}
                  onAddMandate={(id) => setMandateTargetId(id)}
                  busy={busyId === m.id}
                  // Tenants must keep at least one bank account — disable
                  // removal when this is their only bank account.
                  disableRemove={
                    m.type === "us_bank_account" &&
                    methods.filter((x) => x.type === "us_bank_account")
                      .length <= 1
                  }
                />
              ))}
            </div>
          )}
        </div>
      </div>

      <AddCardModal
        open={showAddCard}
        onClose={() => setShowAddCard(false)}
        onSaved={() => {
          setShowAddCard(false);
          flash(t("paymentMethods.successCardAdded"));
          refresh();
        }}
      />

      <AddBankModal
        open={showAddBank}
        onClose={() => setShowAddBank(false)}
        onConnected={() => {
          setShowAddBank(false);
          flash(t("paymentMethods.successBankConnected"));
          refresh();
          // A newly connected bank carries a mandate — may clear the gate.
          refreshUser();
        }}
      />

      <AuthorizeMandateModal
        open={mandateTargetId !== null}
        onClose={() => setMandateTargetId(null)}
        onAuthorize={async () => {
          if (mandateTargetId !== null) {
            await addMandate(mandateTargetId);
          }
        }}
      />
    </div>
  );
};

export default PaymentMethods;
